import { Component, effect, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthLoginResponseSuccessDto } from '../../_generated';
import { AuthActions } from '../../store/actionstypes/auth/auth.actiontype';
import { AuthState, authFeature } from '../../store/reducers/auth.reducer';

@Component({
  standalone: true,
  imports: [TranslatePipe, FormsModule, ReactiveFormsModule],
  selector: 'app-confirm-change-password',
  template: `
    <form [formGroup]="changePasswordForm" (ngSubmit)="onSubmit()" class="mt-8 space-y-6">
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <label for="confirmation-code" class="typo-p2">
            {{ 'change_password.confirmation_code' | translate }}
          </label>
          <input
            id="confirmation-code"
            type="text"
            formControlName="confirmationCode"
            [class]="inputClass('confirmationCode')"
            [placeholder]="'change_password.confirmation_code_placeholder' | translate"
            (focus)="clearBackendErrors()"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label for="new-password" class="typo-p2">
            {{ 'change_password.new_password' | translate }}
          </label>
          <input
            id="new-password"
            type="password"
            formControlName="newPassword"
            [class]="inputClass('newPassword')"
            [placeholder]="'change_password.new_password_placeholder' | translate"
            (focus)="clearBackendErrors()"
          />
        </div>
        <div class="flex flex-col gap-2">
          <label for="confirm-password" class="typo-p2">
            {{ 'change_password.confirm_password' | translate }}
          </label>
          <input
            id="confirm-password"
            type="password"
            formControlName="confirmPassword"
            [class]="inputClass('confirmPassword')"
            [placeholder]="'change_password.confirm_password_placeholder' | translate"
            (focus)="clearBackendErrors()"
          />
        </div>
      </div>

      @if (errorMessage) {
        <div class="text-red-500 text-sm mt-2">
          {{ errorMessage | translate }}
        </div>
      }

      <div class="flex justify-center">
        <button type="submit" [disabled]="changePasswordForm.invalid" class="btn typo-p2 px-8 font-semibold text-white bg-primary-500 rounded-full">
          {{ 'change_password.submit' | translate }}
        </button>
      </div>
    </form>
  `,
})
export class ConfirmChangePasswordComponent {
  private formBuilder = inject(FormBuilder);
  private authStore = inject<Store<AuthState>>(Store);
  private translateService = inject(TranslateService);

  private readonly authError = this.authStore.selectSignal(authFeature.selectError);

  changePasswordForm: FormGroup;
  errorMessage = '';
  session: AuthLoginResponseSuccessDto | null = null;

  readonly changePasswordSession = this.authStore.selectSignal(authFeature.selectSession);
  constructor() {
    this.changePasswordForm = this.formBuilder.group(
      {
        confirmationCode: ['', Validators.required],
        newPassword: ['', [Validators.required, Validators.minLength(8)]],
        confirmPassword: ['', Validators.required],
      },
      { validator: this.passwordMatchValidator },
    );

    effect(() => {
      if (this.changePasswordSession()) {
        this.session = this.changePasswordSession();
      }
    });

    effect(() => {
      const error = this.authError();
      if (error == 'AUTH_CONFIRM_FORGOT_PASSWORD_INVALID_CODE') {
        this.errorMessage = 'change_password.invalid_code';
        this.changePasswordForm.controls['confirmationCode'].setErrors({
          backend: { someProp: error },
        });

        console.log(this.errorMessage);
      }

      if (error == 'AUTH_CONFIRM_FORGOT_PASSWORD_LIMIT_EXCEEDED') {
        this.errorMessage = 'change_password.limit_exceeded';
        this.changePasswordForm.controls['confirmationCode'].setErrors({
          backend: { someProp: error },
        });
        this.changePasswordForm.controls['newPassword'].setErrors({
          backend: { someProp: error },
        });
        this.changePasswordForm.controls['confirmPassword'].setErrors({
          backend: { someProp: error },
        });

        console.log(this.errorMessage);
      }
    });
  }

  clearBackendErrors() {
    if (this.changePasswordForm.controls['confirmationCode'].hasError('backend')) {
      this.changePasswordForm.controls['confirmationCode'].setErrors(null);
      this.changePasswordForm.controls['newPassword'].setErrors(null);
      this.changePasswordForm.controls['confirmPassword'].setErrors(null);
    }
  }

  passwordMatchValidator(form: FormGroup) {
    const newPassword = form.get('newPassword')?.value;
    const confirmPassword = form.get('confirmPassword')?.value;
    return newPassword === confirmPassword ? null : { passwordMismatch: true };
  }

  inputClass(controlName: string): string {
    const baseClass =
      'appearance-none rounded-full relative block w-full px-3 py-2 border placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-indigo-500 focus:z-10 sm:text-sm ';
    const control = this.changePasswordForm.get(controlName);
    return baseClass + (control?.invalid && control?.touched ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-indigo-500');
  }

  onSubmit() {
    if (this.changePasswordForm.valid) {
      const { confirmationCode, newPassword } = this.changePasswordForm.value;
      this.authStore.dispatch(
        AuthActions.confirmChangePassword({
          email: this.session?.email ?? '',
          confirmationCode: confirmationCode,
          newPassword: newPassword,
        }),
      );
    } else {
      this.errorMessage = this.translateService.instant('change_password.form_invalid');
    }
  }
}
